/**
 * ═══════════════════════════════════════════════════════════════════════
 * ConnectionStatusIndicator — Minimal Realtime Sync Status Badge
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Shows a tiny dot + label so staff know the dashboard is live-syncing.
 * No layout shift — positions as a subtle fixed/absolute indicator.
 */

import React from 'react';
import { useTheme } from '../../../theme/ThemeContext';

const STATUS_CONFIG = {
  connected: {
    color: '#22c55e',
    label: 'Live',
    pulse: true,
  },
  disconnected: {
    color: '#ef4444',
    label: 'Offline',
    pulse: false,
  },
  reconnecting: {
    color: '#f59e0b',
    label: 'Reconnecting…',
    pulse: true,
  },
};

export default function ConnectionStatusIndicator({ status, syncMode }) {
  const { isDark } = useTheme();

  // Normalize status to one of 3 states
  const key = status?.startsWith('reconnecting') ? 'reconnecting' : (status || 'disconnected');
  const config = STATUS_CONFIG[key] || STATUS_CONFIG.disconnected;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 500,
        color: isDark ? '#d1d5db' : '#6b7280',
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        transition: 'all 0.3s ease',
        userSelect: 'none',
      }}
      title={`Sync: ${status} ${syncMode ? `(${syncMode})` : ''}`}
    >
      {/* Pulse dot */}
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: config.color,
          display: 'inline-block',
          boxShadow: config.pulse ? `0 0 6px ${config.color}` : 'none',
          animation: config.pulse ? 'ak-pulse 2s infinite' : 'none',
        }}
      />
      <span>{config.label}</span>

      {/* Inline keyframes (no external CSS needed) */}
      <style>{`
        @keyframes ak-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
